import React from 'react';

import Tooltip from '../../components/Tooltip';
import ClearBadge from '../../components/property-summaries/ClearBadge';
import Arc from '../../components/property-summaries/Arc';
import { formatLargeNumber } from '../../utility/formatNumbers';
import RehabInvestmentCriteria from './RehabInvestmentCriteria';
import { renderMoneyAmountShort } from '../../components/formBuilder/utility/renderValue';

export default function RehabKeyMetrics({ propertyData, scenarioData }) {
  const merged = { ...propertyData, ...scenarioData };
  const equityPurchase = merged.equityOnPurchase;
  const newEquityAfterRehab = merged.newEquityBuyAndHold;
  const equityDifference = newEquityAfterRehab - equityPurchase;
  const capRatePurchase = merged.capRate;
  const capRateRehab = merged.marketCapRate;
  const capRateDifference = capRateRehab - capRatePurchase;

  return (
    <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="rounded-lg relative bg-white p-8 flex items-center justify-center gap-6">
        <Arc
          status={capRateRehab > 0 ? 'success' : 'warning'}
          percentage={capRateRehab / 100}
        />
        <div className="flex flex-col items-start">
          <h3 className="text-md text-neutral-600">Cap Rate after rehab</h3>
          <div className="flex items-center gap-2 printing-text-spacing">
            <p className="text-6xl font-serif text-neutral-900 font-medium">
              {formatLargeNumber(capRateRehab)}%
            </p>
            <ClearBadge amount={capRateDifference}>
              {formatLargeNumber(capRateDifference)}%
            </ClearBadge>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <Tooltip
            type="info"
            heading="Cap Rate (Market)"
            text="Cap rate based on the new market value"
            formula={[
              'Cap Rate (Market)',
              '=',
              'Annual Net Operating Income',
              '/',
              'New Market Value',
            ]}
          />
        </div>
      </div>
      <div className="relative">
        <div className="rounded-lg relative bg-white p-6 flex items-center justify-center gap-2 overflow-hidden h-full">
          <div
            className={`w-1.5 h-full absolute left-0 top-0 ${
              newEquityAfterRehab >= 0 ? 'bg-primary-500' : 'bg-warning-500'
            }`}
          ></div>
          <div className="flex flex-col items-start">
            <h3 className="text-md text-neutral-600">Equity after Rehab</h3>
            <div className="flex items-center gap-2 printing-text-spacing">
              <p className="text-6xl font-serif text-neutral-900 font-medium">
                {renderMoneyAmountShort(newEquityAfterRehab)}
              </p>
              <ClearBadge amount={equityDifference}>
                {renderMoneyAmountShort(equityDifference, true)}
              </ClearBadge>
            </div>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <Tooltip
            type="info"
            heading="New Equity"
            text="The new equity in the property after rehab, based on the new value."
            formula={['New Equity', '=', 'New Value', '-', 'Loan Amount']}
          />
        </div>
      </div>
      <RehabInvestmentCriteria
        propertyData={propertyData}
        scenarioData={scenarioData}
      />
    </section>
  );
}
